import './customer-service-dropdown.scss';
import React, { useMemo, useState } from 'react';
import { Input, Menu, Popover } from 'antd';
import { Link } from 'react-router-dom';
import { useNavigate } from 'src/utils/use-navigate';
import { useStores } from '../../store';
import { observer } from 'mobx-react-lite';
import { LevelName, VersionState } from '@src/utils/enums';
import Arrow from '@svg/chevron-down_20.svg?react';
import { ConnectionAndPolicyItemRenderer, serviceComparatorByName } from '../../shared/label-renderer/label-renderer';
import { ConnectionPolicyData } from '@src/dtos/pull-policy.dto';
import SearchIcon from '@svg/search.svg?react';

import { isInitialPolicyModeUndefinedByService } from '../ecm/connection.model';
import { EditConnectionNameModal } from './edit-connection-name-modal';
import { ServiceDto } from '@src/dtos/customer.dto';

export const CustomerServicesDropdown: React.FC = observer(() => {

    const { customerStore, fwVersionStore } = useStores();
    const [isOpen, setIsOpen] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [editModalOpen, setEditModalOpen] = useState(false);

    const [selectedConnection, setSelectedConnection] = useState<ServiceDto | null>(null);
    const navigate = useNavigate();

    const connectionsList: ConnectionPolicyData[] = useMemo(() => {
        let connections = customerStore.customersConnectionsList.map(connectionId => {
            const connection = customerStore.getCustomerServiceData(connectionId);
            const activePolicy = fwVersionStore.allFwVersions[connectionId]?.find(
                policy => policy.versionState === VersionState.ACTIVE
            );
            return !!connection && (!!activePolicy || connection.serviceLevel === LevelName.BRONZE) ? {
                connectionId: connectionId,
                connection: connection,
                policy: activePolicy || {}
            } : null;
        }).filter((_) => !!_ &&
            (searchText.length === 0 ||
                (_.connection.serviceName?.toLowerCase().includes(searchText.toLowerCase()) || _.connection.adiCircuitId.toLowerCase().includes(searchText.toLowerCase()))))
            .sort((a, b) => serviceComparatorByName(a?.connection, b?.connection)) as ConnectionPolicyData[];

        if (connections) {
            const currentIndex = connections.findIndex((item: ConnectionPolicyData) => item.connectionId === customerStore.selectedLocation?.serviceId);
            if (currentIndex > 0) {
                connections = [connections[currentIndex], ...connections.slice(0, currentIndex), ...connections.slice(currentIndex + 1)];
            }
            return connections;
        }
        return [];
    }, [customerStore.selectedLocation, customerStore.customersConnectionsList, fwVersionStore.allFwVersions, searchText]);

    const getEndpoint = (serviceLevel?: LevelName) => serviceLevel === LevelName.BRONZE ? '-bronze' : '';

    // Custom handler for connection click
    const handleConnectionClick = async (item: ConnectionPolicyData) => {
        setIsOpen(false);
        if (!item.connection || item.connection?.serviceId === customerStore.selectedLocation?.serviceId) {
            return;
        }
        const connection = item.connection;
        const isBronze = connection?.serviceLevel === LevelName.BRONZE;
        if (!isBronze && isInitialPolicyModeUndefinedByService(connection)) {
            await customerStore.setCustomPolicy([connection.serviceId]);
        }
        navigate(`/${customerStore.customerId}/${item.connectionId}/dashboard${getEndpoint(connection?.serviceLevel)}`);
    };

    const handleEditClick = (connection: ServiceDto, e: React.MouseEvent) => {
        e.stopPropagation();
        setSelectedConnection(connection);
        setEditModalOpen(true);
    };

    const menuItems = useMemo(() => (
        connectionsList.map((item) => ({
            key: item.connectionId.toString(),
            onClick: () => handleConnectionClick(item),
            label: (
                <div className="connection-menu-item">
                    <ConnectionAndPolicyItemRenderer data={item} onEditClick={handleEditClick} />
                </div>
            )
        }))
    ), [connectionsList]);

    const menu = useMemo(() => (
        <div className="menu-container">
            {connectionsList.length > 1 && <div className="search-customer">
                <Input placeholder={'Search'} value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    allowClear prefix={<SearchIcon />} />
            </div>}
            <Menu selectedKeys={[customerStore.serviceId.toString()]} items={menuItems} />
        </div>
    ), [connectionsList.length, searchText, customerStore.serviceId, menuItems]);


    const onOpenChange = (visible: boolean) => {
        setIsOpen(visible);
    };

    return (
        <>
            {!customerStore.isSingleConnection && (<div className="ecm-link">
                <Link to={`/${customerStore.customerId}/connections`}>Connections</Link>
            </div>)}
            <div className={`customer-service-dropdown`}>
                <Popover
                    content={menu}
                    overlayClassName={'customer-name'}
                    trigger="click"
                    placement={'bottom'}
                    open={isOpen}
                    onOpenChange={onOpenChange}
                    showArrow={false}
                    align={{
                        offset: [0, -10],
                    }}>
                    <div className="connection-name-wrapper">
                        <span className={'connection-name'}>
                            {customerStore.selectedLocation?.serviceName || customerStore.selectedLocation?.adiCircuitId}
                        </span>
                        <Arrow />
                    </div>
                </Popover>
            </div>
            {editModalOpen && selectedConnection && (
                <EditConnectionNameModal
                    connection={selectedConnection}
                    open={editModalOpen}
                    close={() => { setEditModalOpen(false); setSelectedConnection(null); }}
                />
            )}
        </>
    );
});
